/**
 * Pure tree-building algorithm for the corporate structure map.
 * No React — just data transformation.
 *
 * Supports nested subsidiaries: a subsidiary can own other subsidiaries,
 * creating a multi-level corporate structure tree.
 */

import type {
  EntityItem,
  EntityNode,
  Group,
  PrimaryEntity,
  CollectionKey,
} from "../types";
import {
  isInsolvencyAppointeeDetail,
  isInsolvencyAppointeeRole,
} from "@cvx/jobs/summarization/agents/corporate/insolvencyAppointees";

/**
 * Merge two detail strings by splitting into sentences/clauses, normalising,
 * and keeping only unique facts. Prevents the massive duplication that occurs
 * when an officer has multiple corporateEntries rows (Director + Secretary)
 * with overlapping detail text.
 */
function mergeDetails(
  existing: string | null,
  incoming: string | null,
): string | null {
  if (!incoming) return existing;
  if (!existing) return incoming;
  if (existing === incoming) return existing;

  // Split on sentence boundaries (". " or newlines) and normalise
  const split = (s: string) =>
    s
      .split(/(?:\.\s+|\n)/)
      .map((p) => p.replace(/\.+$/, "").trim())
      .filter((p) => p.length > 0);

  const existingParts = split(existing);
  const seen = new Set(existingParts.map((p) => p.toLowerCase()));

  const newParts = split(incoming).filter((p) => !seen.has(p.toLowerCase()));
  if (newParts.length === 0) return existing;

  return [...existingParts, ...newParts].join(". ") + ".";
}

/**
 * Merge two isCurrent flags for the same person at the same entity.
 *
 * A known value beats unknown (null). On a true/false conflict the "former"
 * record wins: an explicit cessation (which is what sets isCurrent=false)
 * outweighs a generic "current" listing — mislabelling a former director as
 * current is far more damaging to a practitioner than the reverse.
 */
export function mergeIsCurrent(
  existing: boolean | null,
  incoming: boolean | null,
): boolean | null {
  if (existing === null) return incoming;
  if (incoming === null) return existing;
  return existing && incoming;
}

export function normalizeAcn(acn: string): string {
  return acn.replace(/\s+/g, "");
}

export function extractAcnFromLabel(label: string): string | null {
  const match = label.match(/ACN\s+([\d\s]+)/i);
  return match ? normalizeAcn(match[1]) : null;
}

export function extractOwnership(detail: string | null): string | null {
  if (!detail) return null;
  const match = detail.match(/(\d+%)/);
  return match ? `${match[1]} owned` : null;
}

export function isAdministrator(entity: EntityItem): boolean {
  return (
    isInsolvencyAppointeeDetail(entity.detail) ||
    isInsolvencyAppointeeRole(entity.name)
  );
}

export function getCollectionKey(
  category: string,
  label: string,
): CollectionKey {
  switch (category) {
    case "officers":
      return "officers";
    case "shareholders":
      return "shareholders";
    case "trusts":
      return "shareholders";
    case "secured_parties":
      return "securedParties";
    case "professionals":
      return "professionals";
    case "related_companies":
      return "relatedEntities";
    case "other":
      if (label.toLowerCase().includes("business name")) return "businessNames";
      return "relatedEntities";
    default:
      return "relatedEntities";
  }
}

export function buildEntityTree(
  primary: PrimaryEntity | null,
  groups: Group[],
): EntityNode | null {
  if (!primary) return null;

  const primaryAcn = primary.acn ? normalizeAcn(primary.acn) : null;

  const makeNode = (
    name: string,
    acn: string | null,
    abn: string | null,
    status: string | null,
    ownership: string | null,
    isPrimary: boolean,
  ): EntityNode => ({
    name,
    acn,
    abn,
    status,
    ownership,
    isPrimary,
    businessNames: [],
    officers: [],
    administrators: [],
    shareholders: [],
    securedParties: [],
    relatedEntities: [],
    professionals: [],
    children: [],
  });

  const root = makeNode(
    primary.name,
    primary.acn,
    primary.abn,
    primary.status,
    null,
    true,
  );

  // ACN → EntityNode lookup (for routing groups and nesting subsidiaries)
  const entityMap = new Map<string, EntityNode>();
  if (primaryAcn) entityMap.set(primaryAcn, root);

  // ---------------------------------------------------------------------------
  // First pass: create all subsidiary nodes and register them in entityMap.
  // We collect them first, then parent them in a second step so that
  // a subsidiary-of-a-subsidiary can find its parent in the map.
  // ---------------------------------------------------------------------------

  interface PendingSub {
    node: EntityNode;
    entityRef: string | null; // ACN of the parent entity (from the group)
  }
  const pendingSubs: PendingSub[] = [];

  for (const group of groups) {
    if (group.category !== "subsidiaries") continue;

    // The group's entityRef tells us WHO owns these subsidiaries.
    // If null/empty → primary entity owns them.
    // If set → another subsidiary owns them (nested structure).
    const parentRef = group.entityRef ? normalizeAcn(group.entityRef) : null;

    for (const entity of group.entities) {
      const childAcn = entity.acn ? normalizeAcn(entity.acn) : null;

      // Skip if we already created a node for this ACN (duplicate subsidiary entry)
      if (childAcn && entityMap.has(childAcn)) continue;

      const child = makeNode(
        entity.name,
        entity.acn,
        entity.abn,
        null,
        extractOwnership(entity.detail),
        false,
      );

      if (childAcn) entityMap.set(childAcn, child);
      pendingSubs.push({ node: child, entityRef: parentRef });
    }
  }

  // Parent each subsidiary under the correct node
  for (const { node, entityRef } of pendingSubs) {
    const found = entityRef ? entityMap.get(entityRef) : undefined;
    const parent = found && found !== node ? found : root;
    parent.children.push(node);
  }

  // ---------------------------------------------------------------------------
  // Second pass: assign non-subsidiary groups to the correct entity node.
  // ---------------------------------------------------------------------------

  for (const group of groups) {
    if (group.category === "subsidiaries") continue;

    // Resolve which entity node this group belongs to. entityRef (or an ACN
    // embedded in the label) scopes the group to a specific entity; no ref
    // means the group belongs to the primary entity.
    const refAcn = group.entityRef
      ? normalizeAcn(group.entityRef)
      : extractAcnFromLabel(group.label);

    const target =
      refAcn === null || refAcn === primaryAcn ? root : entityMap.get(refAcn);

    // A ref that doesn't resolve to a rendered node means the group belongs
    // to an entity outside the subsidiary tree — typically officers or
    // shareholders extracted from a *related* company's own ASIC search.
    // Skip it rather than dumping another company's directors onto the
    // primary entity's card (they remain visible in the Entities table).
    if (!target) continue;

    const key = getCollectionKey(group.category, group.label);
    for (const entity of group.entities) {
      const existing = target[key].find(
        (e) => e.name.toLowerCase().trim() === entity.name.toLowerCase().trim(),
      );
      if (existing) {
        existing.detail = mergeDetails(existing.detail, entity.detail);
        if (entity.sourceDocuments?.length) {
          const existingIds = new Set(
            existing.sourceDocuments?.map((d) => d.id) ?? [],
          );
          const newDocs = entity.sourceDocuments.filter(
            (d) => !existingIds.has(d.id),
          );
          existing.sourceDocuments = [
            ...(existing.sourceDocuments ?? []),
            ...newDocs,
          ];
        }
        existing.isCurrent = mergeIsCurrent(
          existing.isCurrent,
          entity.isCurrent,
        );
      } else {
        target[key].push(entity);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Third pass: split administrators out of professionals/officers.
  // ---------------------------------------------------------------------------

  const splitAdmins = (node: EntityNode) => {
    const profAdmins = node.professionals.filter(isAdministrator);
    node.professionals = node.professionals.filter((e) => !isAdministrator(e));
    node.administrators.push(...profAdmins);

    const officerAdmins = node.officers.filter(isAdministrator);
    node.officers = node.officers.filter((e) => !isAdministrator(e));
    node.administrators.push(...officerAdmins);

    for (const child of node.children) splitAdmins(child);
  };
  splitAdmins(root);

  return root;
}
